import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { api } from '../../../shared/services/API'
import type { Page } from '../../../shared/utils/types'
import { useTablePagination } from '../../../shared/hooks/useTablePagination'
import {
    HELPDESK_RESUMO_KEY,
    HELPDESK_STATUSES,
    HELPDESK_STATUS_LABEL,
    HELPDESK_STATUS_TONE,
    formatDate,
    type HelpdeskResumo,
    type HelpdeskStatus,
    type HelpdeskTicketItem,
} from '../helpdeskModel'

export default function HelpdeskList() {
    const navigate = useNavigate()

    const [rows, setRows] = useState<HelpdeskTicketItem[]>([])
    const [total, setTotal] = useState(0)
    const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination('platform-helpdesk:table-page', 20)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [status, setStatus] = useState<HelpdeskStatus | ''>('')
    const [resumo, setResumo] = useState<HelpdeskResumo | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
        return () => window.clearTimeout(id)
    }, [search])

    useEffect(() => {
        let cancelled = false
        api.get<HelpdeskResumo>('/api/admin/helpdesk/resumo')
            .then((r) => { if (!cancelled) setResumo(r.data) })
            .catch(() => { /* o resumo é acessório — silencioso */ })
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        let cancelled = false
        setIsLoading(true)
        setError('')
        api.get<Page<HelpdeskTicketItem>>('/api/admin/helpdesk/tickets', {
            params: {
                page,
                size: rowsPerPage,
                sort: 'createdAt,desc',
                ...(status ? { status } : {}),
                ...(debouncedSearch ? { q: debouncedSearch } : {}),
            },
        })
            .then((r) => {
                if (cancelled) return
                setRows(r.data.content ?? [])
                setTotal(r.data.totalElements ?? 0)
            })
            .catch(() => { if (!cancelled) setError('Não foi possível carregar os pedidos de suporte.') })
            .finally(() => { if (!cancelled) setIsLoading(false) })
        return () => { cancelled = true }
    }, [page, rowsPerPage, debouncedSearch, status])

    const handleChangePage = (_: unknown, next: number) => setPage(next)
    const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
    }

    const pickStatus = (next: HelpdeskStatus | '') => {
        setStatus((prev) => (prev === next ? '' : next))
        setPage(0)
    }

    const emptyMessage = useMemo(
        () => (debouncedSearch || status
            ? 'Nenhum pedido corresponde aos filtros.'
            : 'Ainda não há pedidos de suporte.'),
        [debouncedSearch, status],
    )

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Suporte</Typography>
                <Typography variant="body2" color="text.secondary">
                    {total} {total === 1 ? 'pedido' : 'pedidos'}{status ? ` · ${HELPDESK_STATUS_LABEL[status]}` : ''}
                </Typography>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
                <Chip
                    label={`Todos${resumo ? ` (${resumo.total})` : ''}`}
                    onClick={() => pickStatus('')}
                    variant={status === '' ? 'filled' : 'outlined'}
                    color={status === '' ? 'primary' : 'default'}
                    size="small"
                />
                {HELPDESK_STATUSES.map((s) => (
                    <Chip
                        key={s}
                        label={`${HELPDESK_STATUS_LABEL[s]}${resumo ? ` (${resumo[HELPDESK_RESUMO_KEY[s]]})` : ''}`}
                        onClick={() => pickStatus(s)}
                        size="small"
                        variant={status === s ? 'filled' : 'outlined'}
                        sx={status === s
                            ? { bgcolor: HELPDESK_STATUS_TONE[s].bg, color: HELPDESK_STATUS_TONE[s].color, fontWeight: 700 }
                            : undefined}
                    />
                ))}
            </Stack>

            <TextField
                size="small"
                placeholder="Pesquisar por assunto, empresa ou quem abriu…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                sx={{ mb: 2, width: { xs: '100%', sm: 380 } }}
            />

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Empresa</TableCell>
                                <TableCell>Assunto</TableCell>
                                <TableCell>Aberto por</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Criado em</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        onClick={() => navigate(`/helpdesk/${row.id}`)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell sx={{ fontWeight: 600 }}>{row.tenantNome || '—'}</TableCell>
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            <Typography variant="body2" noWrap>{row.titulo}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{row.abertoPorNome || '—'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={HELPDESK_STATUS_LABEL[row.status]}
                                                sx={{
                                                    bgcolor: HELPDESK_STATUS_TONE[row.status].bg,
                                                    color: HELPDESK_STATUS_TONE[row.status].color,
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{formatDate(row.criadoEm)}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Linhas por página"
                />
            </Paper>
        </Box>
    )
}
